import { useEffect, useState, useMemo } from "react";
import { Link, useParams, useLocation, useNavigate } from "react-router-dom";
import { api } from "@/lib/api";
import {
  ArrowRight, ChevronRight, Play, Trophy, Target, Feather, Dumbbell, BedDouble, Goal, Cpu, Users,
  Award, GraduationCap, HeartHandshake, Sparkles, Compass, MapPin, Clock, Waves,
} from "lucide-react";

const ICONS = { Target, Trophy, Goal, Feather, Dumbbell, BedDouble, Award, Cpu, Users, HeartHandshake, GraduationCap, Play, Sparkles, Compass, MapPin, Clock, Waves };

// Slug ↔ backend key map
const SLUG_TO_KEY = {
  "facilities":      "facilities",
  "advantage":       "advantage",
  "life-at-academy": "life_at_academy",
  "achievements":    "achievements",
  "pathway":         "pathway",
  "sessions":        "sessions",
};

const NAV_META = [
  { slug: "facilities",      label: "Facilities" },
  { slug: "advantage",       label: "Advantage" },
  { slug: "life-at-academy", label: "Life at Academy" },
  { slug: "achievements",    label: "Achievements" },
  { slug: "pathway",         label: "Pathway" },
  { slug: "sessions",        label: "Sessions" },
  { slug: "fees-structure",  label: "Fees" },
];

export default function AcademySection() {
  const { slug } = useParams();
  const key = SLUG_TO_KEY[slug];
  const nav = useNavigate();
  const [content, setContent] = useState(null);

  useEffect(() => {
    if (!key) { nav("/academy", { replace: true }); return; }
    window.scrollTo({ top: 0, behavior: "instant" });
    api.get("/public/academy").then((r) => setContent(r.data || {})).catch(() => setContent({}));
  }, [slug, key, nav]);

  if (!content) return <div className="min-h-screen flex items-center justify-center text-white/40">Loading…</div>;
  const data = content[key] || {};

  return (
    <div data-testid={`academy-section-${slug}`}>
      <Hero title={data.title} image={data.hero_image} slug={slug} />
      <SectionSubNav current={slug} />
      <WatermarkTitle title={data.title} subtitle={data.subtitle} />

      {data.intro && (
        <div className="max-w-4xl mx-auto px-5 md:px-8 pb-10 md:pb-14">
          <p className="text-white/70 text-base md:text-lg leading-relaxed">{data.intro}</p>
        </div>
      )}

      {slug === "pathway"      && <PathwayPyramid stages={data.stages || []} />}
      {slug === "sessions"     && <SessionsView data={data} />}
      {slug === "achievements" && <AchievementsGrid items={data.items || []} />}
      {(slug === "facilities" || slug === "advantage" || slug === "life-at-academy") && (
        <BlocksList items={data.items || []} />
      )}

      <FinalCTA />
    </div>
  );
}

/* -------- Shared: Hero (breadcrumb style) -------- */
function Hero({ title, image, slug }) {
  const bg = image || "https://images.unsplash.com/photo-1540747913346-19e32dc3e97e?w=2000";
  return (
    <section className="relative border-b border-white/8 overflow-hidden">
      <div className="absolute inset-0">
        <img src={bg} alt="" className="w-full h-full object-cover opacity-40" />
        <div className="absolute inset-0 bg-gradient-to-b from-black/60 via-black/70 to-[#0B1120]" />
      </div>
      <div className="relative max-w-7xl mx-auto px-5 md:px-8 py-16 md:py-24 text-center">
        <h1 className="font-display text-4xl md:text-6xl font-black uppercase tracking-tighter leading-none" data-testid="section-hero-title">
          {title}
        </h1>
        <div className="mt-4 flex items-center justify-center gap-2 text-sm md:text-base font-medium text-white/70">
          <Link to="/" className="hover:brand-orange transition">Home</Link>
          <ChevronRight size={14} className="brand-orange" />
          <Link to="/academy" className="hover:brand-orange transition">Academy</Link>
          <ChevronRight size={14} className="brand-orange" />
          <span className="brand-orange">{title}</span>
        </div>
      </div>
    </section>
  );
}

/* -------- Sub-nav pills -------- */
function SectionSubNav({ current }) {
  return (
    <div className="sticky top-16 z-30 bg-[#0B1120]/95 backdrop-blur border-b border-white/8" data-testid="academy-subnav">
      <div className="max-w-7xl mx-auto px-5 md:px-8 overflow-x-auto">
        <div className="flex items-center gap-1 py-2.5 min-w-max">
          {NAV_META.map((n, i) => (
            <Link
              key={n.slug}
              to={`/academy/${n.slug}`}
              data-testid={`academy-subnav-${n.slug}`}
              className={`px-3 md:px-4 py-1.5 text-[10px] md:text-[11px] font-mono uppercase tracking-widest whitespace-nowrap transition ${
                current === n.slug ? "bg-brand-orange text-black" : "text-white/60 hover:text-white"
              }`}
            >
              {String(i + 1).padStart(2, "0")} · {n.label}
            </Link>
          ))}
        </div>
      </div>
    </div>
  );
}

/* -------- Watermark title (small orange + big black over faded huge letters) -------- */
function WatermarkTitle({ title, subtitle }) {
  if (!title) return null;
  const wm = (title.split(" ").slice(-1)[0] || title).toUpperCase();
  return (
    <div className="relative py-12 md:py-16 text-center overflow-hidden">
      <div aria-hidden className="absolute inset-0 flex items-center justify-center pointer-events-none select-none">
        <div className="font-display text-[100px] md:text-[180px] font-black uppercase tracking-tighter text-white/[0.04] leading-none whitespace-nowrap">
          {wm}
        </div>
      </div>
      <div className="relative">
        <div className="font-mono text-xs md:text-sm uppercase tracking-[0.4em] brand-orange mb-2">{title}</div>
        <div className="font-display text-3xl md:text-5xl font-black uppercase tracking-tighter">{title}</div>
        {subtitle && <p className="mt-4 text-white/60 max-w-2xl mx-auto px-5">{subtitle}</p>}
      </div>
    </div>
  );
}

/* -------- Blocks list (Facilities / Advantage / Life at Academy) -------- */
function BlocksList({ items }) {
  if (!items?.length) {
    return <div className="max-w-4xl mx-auto px-5 py-16 text-center text-white/40 border border-dashed border-white/20 mx-auto max-w-3xl">No content yet.</div>;
  }
  return (
    <div className="max-w-5xl mx-auto px-5 md:px-8 pb-16 space-y-8" data-testid="academy-blocks">
      {items.map((it, i) => <Block key={i} idx={i} item={it} />)}
    </div>
  );
}

function Block({ item, idx }) {
  const Icon = ICONS[item.icon] || Trophy;
  return (
    <article className="border border-white/10 bg-[#0E1526]" data-testid={`block-${idx}`}>
      {/* Blue title bar */}
      <div className="bg-brand-blue px-5 py-3 flex items-center gap-3">
        <div className="h-8 w-8 bg-white/15 flex items-center justify-center">
          <Icon size={15} className="text-white" />
        </div>
        <h3 className="font-display text-lg md:text-xl font-black uppercase tracking-tight text-white leading-tight">
          {item.name || item.title}
        </h3>
      </div>

      {/* Image */}
      {item.image_url && (
        <div className="border-b border-white/10 overflow-hidden">
          <img src={item.image_url} alt={item.name || item.title} className="w-full h-48 md:h-72 object-cover" />
        </div>
      )}

      {/* Content */}
      <div className="p-5 md:p-8 bg-[#F5EFE1] text-[#0B1120]">
        {item.description && <p className="text-sm md:text-base leading-relaxed mb-4">{item.description}</p>}
        {Array.isArray(item.bullets) && item.bullets.length > 0 && (
          <ul className="space-y-2 mt-4">
            {item.bullets.map((b, j) => (
              <li key={j} className="flex items-start gap-2 text-sm md:text-base">
                <span className="inline-block w-2 h-2 bg-brand-blue mt-2 shrink-0" />
                <span>{b}</span>
              </li>
            ))}
          </ul>
        )}
        {item.cta_label && item.cta_url && (
          <a href={item.cta_url} target="_blank" rel="noreferrer"
            className="mt-6 inline-flex items-center gap-2 bg-brand-blue text-white px-4 py-2 font-mono text-xs uppercase tracking-widest hover:opacity-90">
            👉 {item.cta_label}
          </a>
        )}
      </div>
    </article>
  );
}

/* -------- Achievements grid (unchanged rich card) -------- */
function AchievementsGrid({ items }) {
  if (!items?.length) {
    return (
      <div className="max-w-4xl mx-auto px-5 pb-16">
        <div className="border border-dashed border-white/20 p-12 text-center text-white/40">
          <Trophy size={40} className="mx-auto mb-3 opacity-40" />
          <p className="text-sm">Champions coming soon. The wall of fame is being curated by the coaching team.</p>
        </div>
      </div>
    );
  }
  return (
    <div className="max-w-7xl mx-auto px-5 md:px-8 pb-16 grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6" data-testid="achievements-grid">
      {items.map((a, i) => <AchievementCard key={i} idx={i} a={a} />)}
    </div>
  );
}

function AchievementCard({ a, idx }) {
  const [playing, setPlaying] = useState(false);
  const embed = useMemo(() => toEmbedUrl(a.video_url), [a.video_url]);
  return (
    <div className="group border border-white/12 bg-[#131A2B] overflow-hidden hover:border-brand-orange/50 transition-all" data-testid={`achievement-${idx}`}>
      <div className="relative aspect-video overflow-hidden bg-black">
        {playing && embed ? (
          <iframe src={embed} allow="autoplay; encrypted-media; picture-in-picture" allowFullScreen className="w-full h-full" title={a.title} />
        ) : (
          <>
            {a.image_url && <img src={a.image_url} alt={a.title} className="w-full h-full object-cover transition-transform duration-700 group-hover:scale-105" />}
            <div className="absolute inset-0 bg-gradient-to-t from-black/70 to-transparent" />
            {embed && (
              <button onClick={() => setPlaying(true)} data-testid={`achievement-play-${idx}`} className="absolute inset-0 flex items-center justify-center">
                <div className="h-14 w-14 rounded-full bg-brand-orange text-black flex items-center justify-center hover:scale-110 transition">
                  <Play size={20} className="ml-1" />
                </div>
              </button>
            )}
            {a.year && (
              <div className="absolute top-3 left-3 bg-black/70 backdrop-blur px-2 py-1 font-mono text-[10px] uppercase tracking-widest text-white/80">
                {a.year}
              </div>
            )}
          </>
        )}
      </div>
      <div className="p-5">
        {a.athlete_name && <div className="font-mono text-[10px] uppercase tracking-widest brand-orange mb-1">{a.athlete_name}</div>}
        <div className="font-display text-lg font-black uppercase tracking-tight mb-2 leading-tight">{a.title}</div>
        {a.description && <p className="text-sm text-white/60 leading-relaxed">{a.description}</p>}
      </div>
    </div>
  );
}

function toEmbedUrl(url) {
  if (!url) return null;
  try {
    const u = new URL(url);
    if (u.hostname.includes("youtube.com")) { const v = u.searchParams.get("v"); if (v) return `https://www.youtube.com/embed/${v}?autoplay=1`; }
    if (u.hostname === "youtu.be") return `https://www.youtube.com/embed${u.pathname}?autoplay=1`;
    if (u.hostname.includes("vimeo.com")) return `https://player.vimeo.com/video${u.pathname}?autoplay=1`;
    return url;
  } catch { return null; }
}

/* -------- Pathway Pyramid (MAMS style) -------- */
function PathwayPyramid({ stages }) {
  if (!stages?.length) return null;
  // Data is bottom→top (Beginners=01 → Club=05). Reverse so top=narrowest first.
  const topDown = [...stages].reverse();
  const n = topDown.length;
  // Percentages from top (narrowest) to bottom (widest)
  const widthFor = (i) => {
    const min = 22, max = 78;
    return min + ((max - min) * i) / Math.max(n - 1, 1);
  };
  const colors = ["bg-brand-orange", "bg-brand-blue", "bg-brand-orange", "bg-brand-blue", "bg-brand-orange", "bg-brand-blue"];
  return (
    <section className="bg-[#F5EFE1] border-y border-white/8 py-12 md:py-16" data-testid="pathway-pyramid">
      <div className="max-w-6xl mx-auto px-5 md:px-8">
        <h3 className="text-center font-display text-2xl md:text-3xl font-black uppercase text-[#0B1120] mb-10">
          Pathway to Develop
        </h3>
        <div className="flex flex-col items-center gap-1.5 md:gap-2">
          {topDown.map((s, i) => {
            const pct = widthFor(i);
            return (
              <div key={i} className="flex items-center w-full gap-2 md:gap-4" data-testid={`pyramid-row-${i}`}>
                <div className="flex-1 text-right text-[11px] md:text-sm font-medium text-[#0B1120] leading-tight">
                  {s.left_label || s.description}
                </div>
                <div
                  className={`${colors[i % colors.length]} text-white text-center py-3 md:py-4 font-display font-black uppercase text-xs md:text-base tracking-tight`}
                  style={{ flex: `0 0 ${pct}%`, clipPath: "polygon(8% 0, 92% 0, 100% 100%, 0% 100%)" }}
                >
                  {s.title}
                </div>
                <div className="flex-1 text-left text-[11px] md:text-sm font-medium text-[#0B1120] leading-tight">
                  {s.right_label || `${s.age_group} · ${s.duration}`}
                </div>
              </div>
            );
          })}
        </div>
      </div>
    </section>
  );
}

/* -------- Sessions view — curriculum tree + weekly schedule -------- */
function SessionsView({ data }) {
  const curr = data.curriculum || {};
  return (
    <div data-testid="sessions-view">
      <SessionTree curriculum={curr} />

      {data.schedule?.length > 0 && (
        <div className="max-w-7xl mx-auto px-5 md:px-8 py-14">
          <h3 className="font-display text-2xl md:text-3xl font-black uppercase tracking-tight mb-6">
            Weekly Schedule
          </h3>
          {data.note && <div className="mb-6 text-sm text-white/60 italic border-l-2 border-brand-blue pl-4">{data.note}</div>}
          <div className="grid grid-cols-2 sm:grid-cols-4 lg:grid-cols-7 gap-2">
            {["Mon","Tue","Wed","Thu","Fri","Sat","Sun"].map((day) => {
              const rows = (data.schedule || []).filter((r) => r.day === day);
              return (
                <div key={day} className="border border-white/12 bg-[#131A2B] overflow-hidden">
                  <div className="bg-brand-orange text-black font-display text-lg font-black uppercase text-center py-2">{day}</div>
                  <div className="p-3 space-y-3 text-xs min-h-[100px]">
                    {rows.length === 0 && <div className="text-white/30 text-center py-4">Rest</div>}
                    {rows.map((r, i) => (
                      <div key={i}>
                        <div className="font-mono text-[10px] brand-blue">{r.time}</div>
                        <div className="text-white/80 font-medium leading-tight mt-0.5">{r.sport}</div>
                        <div className="text-[10px] text-white/40 mt-0.5">{r.coach}</div>
                      </div>
                    ))}
                  </div>
                </div>
              );
            })}
          </div>
        </div>
      )}
    </div>
  );
}

function SessionTree({ curriculum }) {
  const oneOnOne = curriculum.one_on_one;
  const group = curriculum.group_classes;
  const matches = curriculum.matches;
  if (!oneOnOne && !group && !matches) return null;
  return (
    <section className="bg-[#F5EFE1] border-y border-white/8 py-12 md:py-16" data-testid="session-tree">
      <div className="max-w-7xl mx-auto px-5 md:px-8">
        <div className="grid grid-cols-1 lg:grid-cols-3 gap-6 lg:gap-8">
          {oneOnOne && (
            <TreeBranch title={oneOnOne.title} testid="branch-one-on-one">
              {(oneOnOne.sub_branches || []).map((sb, i) => (
                <div key={i} className="mb-6 last:mb-0">
                  <div className="bg-brand-blue text-white text-center py-2 px-4 font-display font-black uppercase tracking-tight text-sm md:text-base mb-2 rounded">
                    {sb.name}
                  </div>
                  <ul className="space-y-1.5">
                    {(sb.items || []).map((it, j) => (
                      <li key={j} className="bg-brand-orange text-white text-center py-1.5 px-3 text-xs md:text-sm font-medium rounded">{it}</li>
                    ))}
                  </ul>
                </div>
              ))}
            </TreeBranch>
          )}
          {group && (
            <TreeBranch title={group.title} testid="branch-group-classes">
              <ul className="space-y-1.5">
                {(group.items || []).map((it, j) => (
                  <li key={j} className="bg-brand-blue text-white text-center py-2 px-3 text-xs md:text-sm font-medium rounded">{it}</li>
                ))}
              </ul>
            </TreeBranch>
          )}
          {matches && (
            <TreeBranch title={matches.title} testid="branch-matches">
              <ul className="space-y-1.5">
                {(matches.items || []).map((it, j) => (
                  <li key={j} className="bg-brand-orange text-white text-center py-2 px-3 text-xs md:text-sm font-medium rounded">{it}</li>
                ))}
              </ul>
            </TreeBranch>
          )}
        </div>
      </div>
    </section>
  );
}

function TreeBranch({ title, testid, children }) {
  return (
    <div data-testid={testid}>
      <div className="bg-brand-blue text-white text-center py-3 px-5 font-display text-lg md:text-xl font-black uppercase tracking-tight rounded mb-4 shadow-lg shadow-brand-blue/30">
        {title}
      </div>
      <div>{children}</div>
    </div>
  );
}

/* -------- Final CTA -------- */
function FinalCTA() {
  return (
    <section className="border-t border-white/8 bg-brand-orange text-black">
      <div className="max-w-6xl mx-auto px-5 md:px-8 py-16 md:py-20 grid grid-cols-1 md:grid-cols-3 gap-6 items-center">
        <div className="md:col-span-2">
          <div className="font-mono text-xs uppercase tracking-[0.3em] mb-3">// Ready to begin?</div>
          <h2 className="font-display text-3xl md:text-5xl font-black uppercase tracking-tighter leading-[0.95]">
            Book a free trial<br />session this week.
          </h2>
        </div>
        <div className="flex flex-col gap-3 md:items-end">
          <Link to="/enrol" data-testid="section-cta-enrol" className="bg-black text-white px-8 py-4 font-bold text-sm uppercase tracking-widest hover:bg-white/90 hover:text-black inline-flex items-center gap-2">
            Enrol Your Ward <ArrowRight size={16} />
          </Link>
          <Link to="/contact" className="text-black font-mono text-xs uppercase tracking-widest border-b border-black pb-0.5 hover:opacity-70">
            Or contact us →
          </Link>
        </div>
      </div>
    </section>
  );
}
